import { useCallback, useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";

interface Project {
  id: string;
  title: string;
  description?: string;
  status: "planning" | "in_progress" | "on_hold" | "complete";
  completion_percentage: number;
  budget?: number;
  start_date?: string;
  target_end_date?: string;
  contractor_name?: string;
  pending_approvals_count?: number;
  unread_updates_count?: number;
  last_update?: string;
}

export const useClientProjects = (clientId: string) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          id,
          title,
          address,
          status,
          budget,
          start_date,
          estimated_completion_date,
          users!projects_contractor_id_fkey (
            name
          )
        `
        )
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
        setLoading(false);
        return;
      }

      // For each project, get pending approvals count and unread updates
      const projectsWithCounts = await Promise.all(
        (data || []).map(async (project: any) => {
          // Get pending approvals count
          const { count: approvalsCount } = await supabase
            .from("approval_requests")
            .select("*", { count: "exact", head: true })
            .eq("project_id", project.id)
            .eq("status", "pending");

          // Get unread updates count
          const { count: updatesCount } = await supabase
            .from("updates")
            .select("*", { count: "exact", head: true })
            .eq("project_id", project.id)
            .eq("read_by_client", false);

          // Get last update timestamp
          const { data: lastUpdate } = await supabase
            .from("updates")
            .select("timestamp")
            .eq("project_id", project.id)
            .order("timestamp", { ascending: false })
            .limit(1)
            .single();

          return {
            id: project.id,
            title: project.title,
            description: project.address,
            status: project.status,
            completion_percentage: 0,
            budget: project.budget,
            start_date: project.start_date,
            target_end_date: project.estimated_completion_date,
            contractor_name: project.users?.name,
            pending_approvals_count: approvalsCount || 0,
            unread_updates_count: updatesCount || 0,
            last_update: lastUpdate?.timestamp,
          };
        })
      );

      setProjects(projectsWithCounts);
      setLoading(false);
    } catch (err) {
      console.error("Error in fetchProjects:", err);
      setProjects([]);
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      fetchProjects();
    }
  }, [clientId, fetchProjects]);

  return {
    projects,
    loading,
    fetchProjects,
  };
};
